import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { SearchOutlined } from '../../components/icons'
import { Card } from '../../components/ui/Card'
import { DateRangePicker } from '../../components/ui/DateRangePicker'
import type { DateRange, DateRangePreset } from '../../components/ui/DateRangePicker'
import { EmptyState } from '../../components/ui/EmptyState'
import { Spinner } from '../../components/ui/Spinner'
import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'
import { formatSignedPercent } from '../../lib/format/money'
import { OrderTable, OrderTableSkeleton } from './OrderTable'
import type { OrderTableSortColumn, SortDirection } from './OrderTable'
import { StatCard, StatCardSkeleton } from './StatCard'
import {
  PIPELINE_BUCKETS,
  PIPELINE_BUCKET_LABEL,
  SPEND_PERIOD_PHRASE,
  TOOLBAR_FILTERS,
  TOOLBAR_FILTER_LABEL,
  formatMoney,
  formatSpendRangePhrase,
  matchesOrderSearch,
  matchesToolbarFilter,
} from './domain'
import type { OrderSummary, PipelineBucket, ToolbarFilter } from './domain'

type SpendSummary = components['schemas']['SpendSummary']

type LoadState = 'loading' | 'loaded' | 'error'

/**
 * Spend date-range presets (O4/UID-99), matching the design handoff's spend
 * range-picker exactly (`Fictiv Platform.dc.html` ~L7803 `presetDefs`): Last
 * 30 days / Last 90 days / Year to date / Last year / All time — replacing
 * this screen's prior plain `<select>` (All time/30/90/12 months).
 */
const SPEND_DATE_RANGE_PRESETS: readonly DateRangePreset[] = [
  { id: 'last30', label: 'Last 30 days' },
  { id: 'last90', label: 'Last 90 days' },
  { id: 'ytd', label: 'Year to date' },
  { id: 'lastyear', label: 'Last year' },
  { id: 'all', label: 'All time' },
]

/**
 * Resolves a preset id to a concrete `{ from, to }` window; `all` (and any
 * unrecognized id, defensively) is `null` — no window constraint.
 *
 * `last30`/`last90` bound `to` at the exact wall-clock instant `now` (not a
 * midnight) — `from` is `now` minus N days, so both ends share the same
 * time-of-day and neither excludes anything that happened "today so far".
 * `ytd` bounds `from` at the start of the year (inclusive, midnight Jan 1)
 * and `to` at `now` — a fixed start paired with the live instant is correct
 * here, not a mismatch to fix.
 *
 * `lastyear` is the one preset with a FIXED end date (Dec 31 of last year,
 * not "now") — its `to` bound is normalized to {@link endOfDay} so an order
 * placed any time on Dec 31 (not just before midnight) is still inside the
 * window; a plain `new Date(y, 11, 31)` is midnight at the START of Dec 31
 * and would wrongly exclude the rest of that day (Gate 5 review finding).
 */
export function presetRange(id: string): DateRange | null {
  const now = new Date()
  switch (id) {
    case 'last30':
      return { from: daysBefore(now, 30), to: now }
    case 'last90':
      return { from: daysBefore(now, 90), to: now }
    case 'ytd':
      return { from: new Date(now.getFullYear(), 0, 1), to: now }
    case 'lastyear':
      return {
        from: new Date(now.getFullYear() - 1, 0, 1),
        to: endOfDay(new Date(now.getFullYear() - 1, 11, 31)),
      }
    default:
      return null
  }
}

function daysBefore(date: Date, days: number): Date {
  return new Date(date.getTime() - days * 24 * 60 * 60 * 1000)
}

/** The last instant of the given LOCAL calendar day (23:59:59.999) — the
 * inclusive upper bound for a fixed end date in a `{ from, to }` window. */
function endOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)
}

function formatShortDate(date: Date): string {
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Map the current spend window to the `GET /orders?from=&to=` instant bounds
 * that scope the SERVER-authoritative spend aggregate (UID-64). `null` (all
 * time) sends no bounds → the server returns an all-time spend with a `null`
 * changePct (there is no prior period to compare an unbounded window
 * against). The server derives the %-change vs the immediately-preceding
 * equal-length window from the SAME data the client can never see (the
 * capped list), which is exactly why the spend/%-change moved server-side in
 * the reconciliation (ADR-0037).
 */
function rangeToQuery(range: DateRange | null): { from?: string; to?: string } {
  if (!range) return {}
  return { from: range.from.toISOString(), to: range.to.toISOString() }
}

/**
 * Orders list screen (`/orders`, UX spec §1 + screens.md §16). The UID-63 list
 * (table, search, status-filter tabs, clickable pipeline stat cards) and the
 * UID-64 spend view (server-authoritative spend summary + %-change-vs-prior-period)
 * are ONE screen here (ADR-0037): `GET /api/orders?from=&to=` returns `{ items, spend }`
 * from a single server load.
 *
 * The date-range preset scopes the SPEND MONEY figures ONLY — `spend.total`,
 * `spend.orderCount`, `spend.averageOrderValue`, and `spend.changePct` (ADR-0126
 * D1/D20). `items` and `spend.pipeline` are NOT windowed: both are projected from
 * the SAME sorted+capped-200 basis (`items` IS that basis; the pipeline stat cards
 * are computed over it too), so a tile's count agrees with the filtered table by
 * construction (D2/D3) and the "N in flight · M total" header line legitimately
 * excludes draft/cancelled orders — it is not expected to equal the unfiltered row
 * count (D4).
 *
 * REFRESH MODEL (ADR-0126 D5-D9): because only the spend aggregate can differ
 * between two `GET /orders` responses, a date-range change never unmounts the
 * loaded pipeline/spend/table tree — it sets `spendRefreshing`, and on success
 * updates `spend` (and, defensively, `orders`) in place. `loadState` governs ONLY
 * the true initial load and its explicit retry (mount + the two `RetryButton`
 * paths below); a REFRESH failure keeps `loadState === 'loaded'` with the
 * previous, stale-but-labelled `spend` on screen plus an inline retry inside the
 * spend card, rather than tearing the page down into the full-page error state.
 * Keeping the tree mounted is what lets `DateRangePicker.close()`'s existing
 * focus-restore call actually land on its (still-present) trigger — the trigger
 * used to be unmounted mid-interaction, which is why focus fell to `<body>`. A
 * request-sequence guard (`requestSeqRef`) discards any response that is no
 * longer the latest in flight, which only matters now that rapid preset clicks
 * can overlap while the tree stays mounted throughout.
 */
export function OrdersListPage() {
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [orders, setOrders] = useState<readonly OrderSummary[]>([])
  const [spend, setSpend] = useState<SpendSummary | undefined>(undefined)
  const [liveMessage, setLiveMessage] = useState('Loading orders.')

  // ADR-0126 D5-D9: a date-range change is a non-destructive REFRESH, distinct
  // from the initial load/error `loadState`. `spendRefreshFailed` is the D6
  // stale-but-labelled state (loadState stays 'loaded'); `spendLiveMessage` is
  // the Spend card's OWN aria-live region (§2.4), independent of `liveMessage`
  // (the orders-list region), so a date tweak never re-announces the list.
  const [spendRefreshing, setSpendRefreshing] = useState(false)
  const [spendRefreshFailed, setSpendRefreshFailed] = useState(false)
  const [spendLiveMessage, setSpendLiveMessage] = useState('')
  // Set on the first successful load; used to pick 'initial' vs 'refresh' mode
  // for the single [dateRange, load] effect (D5).
  const hasLoadedOnceRef = useRef(false)
  // Monotonic per-call sequence number; a response is applied only if it is
  // still the most recent call issued (D7) — last REQUEST wins, not last
  // arrival.
  const requestSeqRef = useRef(0)

  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<ToolbarFilter>('all')
  // `activePresetId` is `null` for a custom (non-preset) calendar-range
  // selection — the picker highlights no preset in that state. `dateRange`
  // is the single source of truth the fetch keys off of; `null` means "all
  // time" (no window), the default (matches the prior `<select>`'s default).
  // UID-130: default the spend window to "Last 90 days" (matching the design's
  // "Apr 21 – Jul 20 · last 90 days" default) instead of "All time", so the spend
  // card shows a bounded window + its %-change-vs-prior-period on first load. Lazy
  // initializer so `presetRange` samples `now` once at mount, not every render.
  const [activePresetId, setActivePresetId] = useState<string | null>('last90')
  const [dateRange, setDateRange] = useState<DateRange | null>(() => presetRange('last90'))
  const [sortColumn, setSortColumn] = useState<OrderTableSortColumn>('placedAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('descending')

  const tableRef = useRef<HTMLDivElement | null>(null)

  const spendTriggerLabel = dateRange
    ? `${formatShortDate(dateRange.from)} – ${formatShortDate(dateRange.to)}`
    : 'All time'

  const spendPeriodPhrase =
    (activePresetId && SPEND_PERIOD_PHRASE[activePresetId]) ||
    (dateRange ? formatSpendRangePhrase(dateRange) : 'all time')

  // Gate 5 W4: `load` reads the CURRENT phrase off this ref instead of
  // taking it as a parameter, so the fetch effect below can depend on
  // `dateRange` alone — the only state that should ever trigger a network
  // request — without needing to smuggle `spendPeriodPhrase` in as a
  // fetch-effect dependency just to hand it to the success-message string.
  // A plain render-time assignment (not an effect) keeps this ref current
  // for whichever request is in flight when it resolves.
  const spendPeriodPhraseRef = useRef(spendPeriodPhrase)
  spendPeriodPhraseRef.current = spendPeriodPhrase

  /**
   * `mode: 'initial'` — mount, and the two `RetryButton` paths in the true
   * error state below — behaves exactly as before this story: sets
   * `loadState`, shows the full skeleton/error UI. `mode: 'refresh'` — every
   * subsequent date-range change — never touches `loadState`: the loaded tree
   * stays mounted throughout, and only `spendRefreshing`/`spendRefreshFailed`
   * (D6) change. `moneyLabel` on success and the D9 live-region copy are
   * resolved from the RESPONSE and `spendPeriodPhraseRef.current` (Gate 5 W4
   * — the phrase at the moment the request was issued, read off a ref rather
   * than taken as a parameter so this callback's identity/deps never need to
   * change with it), never from a stale closure over page state.
   */
  const load = useCallback(async (range: DateRange | null, mode: 'initial' | 'refresh') => {
    const seq = ++requestSeqRef.current

    if (mode === 'initial') {
      setLoadState('loading')
      setLiveMessage('Loading orders.')
    } else {
      setSpendRefreshing(true)
      setSpendRefreshFailed(false)
      setSpendLiveMessage('Updating spend summary.')
    }

    try {
      const { data, error } = await apiClient.GET('/orders', {
        params: { query: rangeToQuery(range) },
      })
      // D7: a stale (superseded) response is discarded outright — no state
      // change at all, not even an error, since a NEWER request is already
      // in flight or has already landed.
      if (seq !== requestSeqRef.current) return

      if (data) {
        setOrders(data.items)
        setSpend(data.spend)
        if (mode === 'initial') {
          hasLoadedOnceRef.current = true
          setLoadState('loaded')
          // Gate 5 LOW-2: tidy the (currently unreachable, since this is the
          // FIRST successful load) refresh-only flags too, now that the
          // state machine has grown from 1 flag to 4 — an 'initial' success
          // should never leave a stale refreshing/failed flag set.
          setSpendRefreshing(false)
          setSpendRefreshFailed(false)
          setLiveMessage(
            data.items.length === 0
              ? 'Orders loaded. No orders yet.'
              : `Orders loaded — ${data.items.length}.`,
          )
        } else {
          setSpendRefreshing(false)
          setSpendRefreshFailed(false)
          const moneyLabel = formatMoney(Number(data.spend.total), data.spend.currency)
          setSpendLiveMessage(`Spend updated — ${moneyLabel} for ${spendPeriodPhraseRef.current}.`)
        }
        return
      }
      if (error && (error as { status?: number }).status === 401) {
        // Gate 5 LOW-2: handled globally by authMiddleware's session
        // invalidation, but clear the refresh spinner regardless — in any
        // case where the redirect doesn't fire immediately, this must not
        // leave `spendRefreshing` stuck true forever.
        if (mode === 'refresh') {
          setSpendRefreshing(false)
        }
        return
      }
      if (mode === 'initial') {
        setLoadState('error')
        setLiveMessage('Orders failed to load.')
      } else {
        // D6: a refresh FAILURE must not tear the page down — keep
        // loadState === 'loaded' and the previous spend on screen.
        setSpendRefreshing(false)
        setSpendRefreshFailed(true)
        setSpendLiveMessage('Spend summary failed to update. Previous figures shown.')
      }
    } catch {
      if (seq !== requestSeqRef.current) return
      if (mode === 'initial') {
        setLoadState('error')
        setLiveMessage('Orders failed to load.')
      } else {
        setSpendRefreshing(false)
        setSpendRefreshFailed(true)
        setSpendLiveMessage('Spend summary failed to update. Previous figures shown.')
      }
    }
  }, [])

  // One effect for both the mount fetch and every subsequent date-range
  // refresh (D5) — the mode is picked from `hasLoadedOnceRef`, set on the
  // first successful load, rather than a second mechanism (e.g. a
  // skip-first-run effect) for the same decision.
  //
  // Gate 5 W4: deps are `[dateRange, load]` ONLY — `dateRange` is the single
  // state that should ever trigger a network request. `spendPeriodPhrase`
  // used to be listed here too (with an unexplained lint suppression) purely
  // so `load` could stamp it into the success message; it never needed to
  // be a FETCH trigger, and coupling network requests to a display string
  // meant editing `SPEND_PERIOD_PHRASE`'s copy could change when requests
  // fire. `load` now reads the phrase off `spendPeriodPhraseRef` instead.
  useEffect(() => {
    void load(dateRange, hasLoadedOnceRef.current ? 'refresh' : 'initial')
  }, [dateRange, load])

  function handlePresetSelect(id: string) {
    setActivePresetId(id)
    setDateRange(presetRange(id))
  }

  function handleRangeSelect(range: DateRange) {
    setActivePresetId(null)
    setDateRange(range)
  }

  const filteredOrders = useMemo(() => {
    const query = search.trim().toLowerCase()
    return orders
      .filter((order) => matchesToolbarFilter(order.status, activeFilter))
      .filter((order) => matchesOrderSearch(order, query))
      .slice()
      .sort((a, b) => {
        const dir = sortDirection === 'ascending' ? 1 : -1
        if (sortColumn === 'total') {
          return (Number(a.total.amount) - Number(b.total.amount)) * dir
        }
        const aTime = a.placedAt ? new Date(a.placedAt).getTime() : 0
        const bTime = b.placedAt ? new Date(b.placedAt).getTime() : 0
        return (aTime - bTime) * dir
      })
  }, [orders, activeFilter, search, sortColumn, sortDirection])

  function pipelineCountFor(bucket: PipelineBucket): number {
    return spend?.pipeline.find((entry) => entry.bucket === bucket)?.count ?? 0
  }

  const pipelineInFlight = pipelineCountFor('in_production') + pipelineCountFor('in_transit')
  const pipelineTotalCount = PIPELINE_BUCKETS.reduce((sum, b) => sum + pipelineCountFor(b), 0)

  function handleSort(column: OrderTableSortColumn) {
    if (column === sortColumn) {
      setSortDirection((current) => (current === 'ascending' ? 'descending' : 'ascending'))
    } else {
      setSortColumn(column)
      // Both sortable columns (placedAt, total) default to newest/highest-first
      // on first click.
      setSortDirection('descending')
    }
  }

  function handleStatCardSelect(bucket: PipelineBucket) {
    setActiveFilter((current) => (current === bucket ? 'all' : bucket))
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function handleClearFilters() {
    setSearch('')
    setActiveFilter('all')
  }

  const hasAnyOrdersAtAll = orders.length > 0
  const hasAnySearchOrFilterActive = search.trim() !== '' || activeFilter !== 'all'

  return (
    <section aria-labelledby="orders-list-heading">
      {/* UID-449 item 3: `orders-list-heading` carries the design's 16px gap
      below the title (`Fictiv Platform.dc.html:3743`). It is a page-scoped
      modifier rather than a change to `.display-heading`, which ~45 headings
      across the app share and several pages already space themselves — same
      pattern as `workspace-heading` / `login-heading`. */}
      <h1 id="orders-list-heading" className="display-heading orders-list-heading">
        Orders
      </h1>
      <p aria-live="polite" className="sr-only">
        {liveMessage}
      </p>

      {loadState === 'loading' && (
        <>
          <div className="pipeline-spend-row">
            <Card className="pipeline-card" aria-hidden="true">
              <div className="stat-card-grid">
                {PIPELINE_BUCKETS.map((bucket) => (
                  <StatCardSkeleton key={bucket} />
                ))}
              </div>
            </Card>
            <Card className="spend-summary-card" aria-hidden="true">
              <div className="spend-summary-skeleton" />
            </Card>
          </div>
          <OrderTableSkeleton />
        </>
      )}

      {loadState === 'error' && (
        <>
          <Card className="pipeline-error-panel">
            <p role="alert">
              Order summary unavailable right now —{' '}
              <RetryButton onRetry={() => load(dateRange, 'initial')} />
            </p>
          </Card>
          <div className="data-table-wrapper">
            <div role="alert" className="table-error-panel">
              <p>Couldn&rsquo;t load your orders.</p>
              <RetryButton onRetry={() => load(dateRange, 'initial')} label="Try again" />
            </div>
          </div>
        </>
      )}

      {loadState === 'loaded' && !hasAnyOrdersAtAll && (
        <EmptyState
          title="You haven't placed any orders yet"
          action={
            <Link to="/catalog" className="btn btn-primary btn-md">
              Browse the catalog
            </Link>
          }
        />
      )}

      {loadState === 'loaded' && hasAnyOrdersAtAll && spend && (
        <>
          {/* 06-orders.png: "Order pipeline" + "Spend" sit in one flex row
          that wraps to stacked cards at narrower widths (the screenshot's
          own viewport is narrow enough to show them stacked already). */}
          <div className="pipeline-spend-row">
            <Card className="pipeline-card">
              <div className="pipeline-card-header">
                <span className="pipeline-card-title">Order pipeline</span>
                <span className="pipeline-card-summary">
                  {pipelineInFlight} in flight · {pipelineTotalCount} total
                </span>
              </div>
              <div className="stat-card-grid">
                {PIPELINE_BUCKETS.map((bucket) => {
                  const entry = spend.pipeline.find((e) => e.bucket === bucket)
                  return (
                    <StatCard
                      key={bucket}
                      bucket={bucket}
                      label={PIPELINE_BUCKET_LABEL[bucket]}
                      count={entry?.count ?? 0}
                      units={entry?.units ?? 0}
                      amount={Number(entry?.amount ?? '0')}
                      currency={spend.currency}
                      active={activeFilter === bucket}
                      onSelect={handleStatCardSelect}
                    />
                  )
                })}
              </div>
            </Card>

            {/* Gate 5 C2 fix: aria-busy no longer sits on this whole Card.
            Per WAI-ARIA, AT is expected to WITHHOLD announcing changes
            inside an aria-busy="true" subtree — and the D9 spend live
            region below is a DESCENDANT of this Card, so busy-on-the-Card
            silently suppressed the very "Updating spend summary." progress
            announcement D9 exists to deliver. Scoping aria-busy to a
            wrapper `<span>` around just the DateRangePicker trigger (never
            aria-hidden — it holds the focused trigger — and never disabled,
            which would drop focus off it) restores the UX spec's placement
            (busy on the trigger itself) without touching DateRangePicker.tsx
            (D10), and keeps the live region OUTSIDE the busy subtree. */}
            <Card className="spend-summary-card">
              <div className="spend-summary-header">
                <span className="spend-summary-title-group">
                  <span className="spend-summary-title">Spend</span>
                  {/* D8: the additive spinner is the ONLY visual delta during a
                  refresh — never a dim/opacity change on the figures below
                  (UX spec §2.1's contrast math), and never anything on the
                  pipeline card, whose counts cannot change after D1/D2. */}
                  {spendRefreshing && <Spinner size="sm" aria-hidden="true" />}
                </span>
                <span aria-busy={spendRefreshing || undefined}>
                  <DateRangePicker
                    label="Date range for spend summary"
                    presets={SPEND_DATE_RANGE_PRESETS}
                    activePresetId={activePresetId}
                    value={dateRange}
                    triggerLabel={spendTriggerLabel}
                    onPresetSelect={handlePresetSelect}
                    onRangeSelect={handleRangeSelect}
                  />
                </span>
              </div>
              <span className="spend-summary-primary">
                {formatMoney(Number(spend.total), spend.currency)}
              </span>
              <span className="spend-summary-subline">
                {spend.orderCount} order{spend.orderCount === 1 ? '' : 's'} · avg{' '}
                {formatMoney(Number(spend.averageOrderValue), spend.currency)} · {spendPeriodPhrase}
              </span>
              {spendRefreshFailed ? (
                // D6: a refresh failure keeps the previous spend numbers above
                // AS-IS (stale-but-labelled) and surfaces the retry in the same
                // slot `.spend-summary-delta` already reserves for the
                // %-change badge — no new min-height, no new reservation.
                <span className="spend-summary-delta">
                  {/* Gate 5 review: the visible retry copy now matches the
                  live-region string verbatim (both trace to the UX spec,
                  which wins over the ADR's differing wording here) — the two
                  surfaces no longer disagree on what happened. */}
                  Spend summary failed to update. Previous figures shown.{' '}
                  <RetryButton onRetry={() => load(dateRange, 'refresh')} />
                </span>
              ) : (
                // UID-64 completes what UID-63 stubbed: the server-derived
                // %-change-vs-prior-period badge. Absent for the all-time window
                // (no prior period → server returns changePct null). Color is never
                // the only signal (WCAG 2.2 AA): tone + glyph + signed-percent text
                // each independently convey direction; exactly 0% is neutral.
                <ChangeBadge changePct={spend.changePct} />
              )}
              {/* ADR-0126 D9/UX spec §2.4: the Spend card's OWN aria-live
              region, independent of the orders-list `liveMessage` above — a
              date-range refresh must announce spend-only progress, never
              re-announce "Orders loaded" for a list that didn't change. */}
              <p aria-live="polite" className="sr-only">
                {spendLiveMessage}
              </p>
            </Card>
          </div>

          <div className="orders-toolbar">
            <label className="orders-search">
              <span className="sr-only">Search orders by ID, name, or part</span>
              <SearchOutlined aria-hidden="true" />
              <input
                type="search"
                placeholder="Search orders by ID, name, or part"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <div role="tablist" aria-label="Filter orders by status" className="filter-chip-tabs">
              {TOOLBAR_FILTERS.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  role="tab"
                  aria-selected={activeFilter === filter}
                  tabIndex={activeFilter === filter ? 0 : -1}
                  className={
                    activeFilter === filter ? 'filter-chip filter-chip-active' : 'filter-chip'
                  }
                  onClick={() => setActiveFilter(filter)}
                >
                  {TOOLBAR_FILTER_LABEL[filter]}
                </button>
              ))}
            </div>
            {/* UID-212 follow-up: the design shows the order count at the right of the toolbar row
            (`Fictiv Platform.dc.html` — "13 orders"). Reflects the orders actually shown in the
            list — i.e. after the active search + status-filter are applied. */}
            <span className="orders-toolbar-count">
              {filteredOrders.length} {filteredOrders.length === 1 ? 'order' : 'orders'}
            </span>
          </div>

          <div ref={tableRef}>
            <OrderTable
              orders={filteredOrders}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              hasAnySearchOrFilterActive={hasAnySearchOrFilterActive}
              onClearFilters={handleClearFilters}
            />
          </div>
        </>
      )}
    </section>
  )
}

/**
 * The %-change-vs-prior-period line (UID-64). `null` (all-time window / zero
 * prior spend) renders the reserved empty slot so the card keeps its mock
 * height.
 *
 * UID-449 item 4: this is **bare colored text**, not a chip. The design
 * (`Fictiv Platform.dc.html:3811`, styled by `deltaStyle` at `:10042`) renders
 * a plain element with only `font-size:11.5px; font-weight:700;
 * margin-top:6px; color:<teal|red>` — no background, border, padding or
 * radius. The earlier build wrapped it in `<Chip>`, which added all four.
 *
 * Exactly 0% is NEUTRAL (a `±` glyph, no up/down claim). That state is a
 * deliberate, documented DEVIATION from the design, which treats `spendDelta
 * >= 0` as an increase and would render a teal "▲ +0%" — a claim that an
 * increase happened when it did not. Retained on purpose; do not "fix" it back
 * to match the prototype without reading this comment.
 *
 * Color is never the sole signal (WCAG 2.2 AA 1.4.1): the ▲/▼/± glyph and the
 * signed-percent text each convey direction independently of the tone class.
 */
function ChangeBadge({ changePct }: { readonly changePct: number | null }) {
  if (changePct === null) {
    return <span className="spend-summary-delta" aria-hidden="true" />
  }
  if (changePct === 0) {
    return (
      <span className="spend-summary-delta spend-summary-delta-neutral">
        <span aria-hidden="true">±</span> {formatSignedPercent(changePct)} vs prior period
      </span>
    )
  }
  const isUp = changePct > 0
  return (
    <span
      className={`spend-summary-delta ${isUp ? 'spend-summary-delta-up' : 'spend-summary-delta-down'}`}
    >
      <span aria-hidden="true">{isUp ? '▲' : '▼'}</span> {formatSignedPercent(changePct)} vs prior
      period
    </span>
  )
}

function RetryButton({
  onRetry,
  label = 'Try again',
}: { readonly onRetry: () => void; readonly label?: string }) {
  return (
    <button type="button" className="btn-link" onClick={onRetry}>
      {label}
    </button>
  )
}
